import { useMemo, useState } from 'react';
import { Paperclip, ShieldCheck } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTagRequestsQuery } from '@/client/api/tag-requests/tag-requests.query';
import type { TagRequest, TagRequestStatusFilter } from '@/client/types/tag-requests';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import Pagination from '@/client/components/common/Pagination';
import TagRequestReviewDrawer from '@/client/components/admin/TagRequest/TagRequestReviewDrawer';
import {
  ITEM_STATUS_BADGE,
  REQUEST_STATUS_BADGE,
  REQUEST_STATUS_LABEL,
  listingLabel,
  personLabel,
  requesterLabel,
  titleCaseTag,
} from '@/client/components/admin/TagRequest/tagRequestStatus';

const PAGE_SIZE = 25;
const TABS: { id: TagRequestStatusFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'partially_approved', label: 'Partly approved' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'withdrawn', label: 'Withdrawn' },
  { id: 'all', label: 'All' },
];
const HEADINGS = ['Listing', 'Tags', 'Requested by', 'Files', 'Submitted', 'Status', ''];

const TagRequestsPage = () => {
  useBreadcrumbs([{ label: 'Listings' }, { label: 'Tag requests' }]);

  const { filters, page, handleFilterChange, handlePageChange } = useListFilters<{ status: TagRequestStatusFilter }>({
    initialFilters: { status: 'open' },
    initialLimit: PAGE_SIZE,
  });

  // Assets are only needed for the file count; the drawer fetches the full detail.
  const params = useMemo(
    () => ({ status: filters.status, page, page_size: PAGE_SIZE, include_assets: 1 as const }),
    [filters.status, page],
  );
  const { data, isLoading, isError } = useTagRequestsQuery(params);
  const [selected, setSelected] = useState<TagRequest | null>(null);
  const requests = data?.requests ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <ShieldCheck className="h-6 w-6 text-blue-700" />
            Tag requests
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Listers asking for protected tags such as “Made in Ghana” or certification marks. Read the case and
            the evidence, then decide each tag on its own — approved tags attach to the listing straight away.
          </p>
        </div>

        <div className="flex flex-wrap gap-1 rounded-lg bg-white p-1 shadow-sm">
          {TABS.map((tab) => {
            const active = filters.status === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleFilterChange('status', tab.id)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {HEADINGS.map((heading, index) => (
                  <th
                    key={heading || index}
                    className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${heading ? 'text-left' : 'text-right'}`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    {HEADINGS.map((__, cell) => (
                      <td key={cell} className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load tag requests. Try again.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && requests.length === 0 && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-gray-500">
                    No tag requests match this filter.
                  </td>
                </tr>
              )}

              {requests.map((req) => (
                <tr key={req.etrid} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelected(req)}>
                  <td className="max-w-xs px-6 py-4">
                    <span className="block truncate text-sm font-medium text-gray-900" title={listingLabel(req)}>
                      {listingLabel(req)}
                    </span>
                    <span className="block text-xs text-gray-500">request #{req.etrid} · eid {req.eid}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(req.items ?? []).map((i) => (
                        <span
                          key={i.etriid}
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ITEM_STATUS_BADGE[i.status]}`}
                          title={i.status}
                        >
                          {titleCaseTag(i.tag_name)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                    <span className="block">{requesterLabel(req)}</span>
                    {req.oid && req.requester && (
                      <span className="block text-xs text-gray-500">by {personLabel(req.requester)}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                      {req.assets?.length ?? 0}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {req.date_submitted || req.date_added
                      ? moment(req.date_submitted ?? req.date_added).format('YYYY-MM-DD HH:mm')
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${REQUEST_STATUS_BADGE[req.status]}`}>
                      {REQUEST_STATUS_LABEL[req.status] ?? req.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(req);
                      }}
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
            <Pagination total={data?.total ?? 0} page={page} limit={PAGE_SIZE} setPage={handlePageChange} showButtons />
          </div>
        </div>
      </div>

      {selected && <TagRequestReviewDrawer summary={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default TagRequestsPage;
